import {
  loadStripe,
  Stripe,
  StripeElements,
  PaymentIntent,
  StripeError,
} from '@stripe/stripe-js';
import { PaymentsConfig } from '../../shared/types';
import { ERROR_MESSAGES } from '../../shared/constants';

let stripeInstance: Promise<Stripe | null> | null = null;

export const getStripe = (publishableKey: string): Promise<Stripe | null> => {
  if (!stripeInstance) {
    stripeInstance = loadStripe(publishableKey);
  }
  return stripeInstance;
};

export const initializeStripe = async (
  config: PaymentsConfig
): Promise<Stripe | null> => {
  if (!config.publishableKey) {
    console.error(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
    return null;
  }

  try {
    const stripe = await getStripe(config.publishableKey);
    return stripe;
  } catch (error) {
    console.error('Failed to initialize Stripe:', error);
    return null;
  }
};

export const createCheckoutSession = async (params: {
  priceId: string;
  customerId?: string;
  customerEmail?: string;
  successUrl: string;
  cancelUrl: string;
  allowPromotionCodes?: boolean;
  billingAddressCollection?: 'auto' | 'required';
  metadata?: Record<string, string>;
  trialPeriodDays?: number;
}): Promise<{ sessionId: string } | { error: string }> => {
  try {
    const response = await fetch('/api/payments/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return { error: errorData.error || ERROR_MESSAGES.CHECKOUT_FAILED };
    }

    const data = await response.json();
    return { sessionId: data.sessionId };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error.message : ERROR_MESSAGES.CHECKOUT_FAILED,
    };
  }
};

export const createPaymentIntent = async (params: {
  amount: number;
  currency: string;
  customerId?: string;
  description?: string;
  metadata?: Record<string, string>;
}): Promise<{ clientSecret: string } | { error: string }> => {
  try {
    const response = await fetch('/api/payments/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return { error: errorData.error || 'Failed to create payment intent' };
    }

    const data = await response.json();
    return { clientSecret: data.client_secret };
  } catch (error) {
    return {
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create payment intent',
    };
  }
};

export const createPortalSession = async (params: {
  customerId: string;
  returnUrl: string;
}): Promise<{ url: string } | { error: string }> => {
  try {
    const response = await fetch('/api/payments/create-portal-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Network error' }));
      return { error: errorData.error || 'Failed to create portal session' };
    }

    const data = await response.json();
    return { url: data.url };
  } catch (error) {
    return {
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create portal session',
    };
  }
};

export const confirmPayment = async (
  stripe: Stripe,
  elements: StripeElements,
  clientSecret: string,
  options: {
    returnUrl?: string;
    receiptEmail?: string;
  } = {}
): Promise<{
  success: boolean;
  error?: string;
  paymentIntent?: PaymentIntent;
}> => {
  try {
    const result = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: options.returnUrl || window.location.href,
        receipt_email: options.receiptEmail,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      return { success: false, error: result.error.message };
    }

    if (result.paymentIntent?.status === 'succeeded') {
      return { success: true, paymentIntent: result.paymentIntent };
    }

    return { success: false, error: 'Payment was not completed' };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : ERROR_MESSAGES.PAYMENT_FAILED,
    };
  }
};

export const redirectToCheckout = async (
  stripe: Stripe,
  sessionId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const result = await stripe.redirectToCheckout({ sessionId });

    if (result.error) {
      return { success: false, error: result.error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : ERROR_MESSAGES.CHECKOUT_FAILED,
    };
  }
};

export const formatStripeError = (
  error: StripeError | { code?: string; message?: string } | string
): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error?.message) {
    return error.message;
  }

  if (error?.code) {
    const errorMessages: Record<string, string> = {
      card_declined: 'Your card was declined.',
      expired_card: 'Your card has expired.',
      incorrect_cvc: "Your card's security code is incorrect.",
      processing_error: 'An error occurred while processing your card.',
      incorrect_number: 'Your card number is incorrect.',
    };

    return errorMessages[error.code] || `Payment failed: ${error.code}`;
  }

  return 'An unexpected error occurred.';
};
